import React, { useEffect, useState, useCallback } from "react";
import { Boxes, Loader2, Search, ArrowUp, ArrowDown, RefreshCw, Terminal, Layers, Activity, Calendar, Server } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { Pagination } from "./Pagination";

interface AssetContainersProps {
    asset_id: string;
    client_id: string;
}

export interface ContainerData {
    id: number;
    created: string;
    updated: string;
    is_deleted?: boolean;
    agent_id?: number;
    client_id?: number;
    organization_id?: number;
    asset_id?: number;
    container_id: string;
    name: string;
    image: string;
    image_id?: string;
    command?: string;
    state: string;
    status: string;
    container_created?: string;
    pid?: string;
    runtime: string;
    image_size_bytes?: number | null;
}

const AssetContainers: React.FC<AssetContainersProps> = ({
    asset_id,
    client_id
}) => {
    const [containerData, setContainerData] = useState<ContainerData[]>([]);
    const [loading, setLoading] = useState(true);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    // Search state
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchContainers = useCallback(async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;
            const queryObj: any = { asset_id, client_id };
            if (debouncedSearchTerm) {
                queryObj.$or = [
                    { name: { $ilike: `%${debouncedSearchTerm}%` } },
                    { container_id: { $ilike: `%${debouncedSearchTerm}%` } },
                    { image: { $ilike: `%${debouncedSearchTerm}%` } },
                    { runtime: { $ilike: `%${debouncedSearchTerm}%` } },
                    { state: { $ilike: `%${debouncedSearchTerm}%` } },
                    { status: { $ilike: `%${debouncedSearchTerm}%` } }
                ];
            }
            const params: any = {
                limit,
                skip,
                query: JSON.stringify(queryObj),
            };
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }
            const response = await baseapi.get("/asset_containers", { params });

            if (response.data?.status) {
                setContainerData(response.data.data ?? []);
                setTotalRecords(response.data.total ?? 0);
            } else {
                setContainerData([]);
                setTotalRecords(0);
            }
        } catch (err: any) {
            showError(err.message || "Failed to fetch containers");
            setContainerData([]);
            setTotalRecords(0);
        }
        setLoading(false);
    }, [asset_id, client_id, currentPage, limit, debouncedSearchTerm, sortConfig]);

    useEffect(() => {
        fetchContainers();
    }, [fetchContainers]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1);
    };

    const SortIcon = ({ column }: { column: string }) => {
        if (sortConfig?.key !== column) {
            return (
                <div className="opacity-0 group-hover:opacity-40 transition-opacity">
                    <ArrowUp size={10} className="text-slate-400" />
                </div>
            );
        }
        return sortConfig.direction === "asc"
            ? <ArrowUp size={10} className="text-orange-500" />
            : <ArrowDown size={10} className="text-orange-500" />;
    };

    const getRuntimeBadge = (runtime: string) => {
        const rt = runtime?.toLowerCase() || "";
        if (rt.includes("docker")) {
            return (
                <span className="px-2 py-0.5 bg-sky-500/10 text-sky-400 border border-sky-500/20 rounded text-[10px] font-bold uppercase tracking-wider">
                    {runtime}
                </span>
            );
        }
        if (rt.includes("podman")) {
            return (
                <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded text-[10px] font-bold uppercase tracking-wider">
                    {runtime}
                </span>
            );
        }
        if (rt.includes("containerd")) {
            return (
                <span className="px-2 py-0.5 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 rounded text-[10px] font-bold uppercase tracking-wider">
                    {runtime}
                </span>
            );
        }
        return (
            <span className="px-2 py-0.5 bg-slate-500/10 text-slate-400 border border-slate-500/20 rounded text-[10px] font-bold uppercase tracking-wider">
                {runtime || "-"}
            </span>
        );
    };

    const getStateBadge = (state: string) => {
        const st = state?.toLowerCase() || "";
        if (st === "running") {
            return (
                <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded text-[10px] font-bold uppercase tracking-wider shadow-[0_0_8px_rgba(52,211,153,0.15)]">
                    {state}
                </span>
            );
        }
        if (st === "exited") {
            return (
                <span className="px-2 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded text-[10px] font-bold uppercase tracking-wider">
                    {state}
                </span>
            );
        }
        if (st === "paused") {
            return (
                <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded text-[10px] font-bold uppercase tracking-wider">
                    {state}
                </span>
            );
        }
        if (st === "restarting" || st === "created") {
            return (
                <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded text-[10px] font-bold uppercase tracking-wider">
                    {state}
                </span>
            );
        }
        return (
            <span className="px-2 py-0.5 bg-gray-500/10 text-gray-400 border border-gray-500/20 rounded text-[10px] font-bold uppercase tracking-wider">
                {state || "-"}
            </span>
        );
    };

    const formatDateFromUTC = (dateStr?: string) => {
        if (!dateStr) return "-";
        try {
            let str = dateStr.trim();
            if (str.endsWith("UTC")) {
                str = str.substring(0, str.length - 3).trim();
            }
            str = str.replace(/(\.\d{3})\d+/, "$1");
            if (!str.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(str)) {
                if (str.includes("T")) {
                    str = `${str}Z`;
                } else if (str.includes(" ")) {
                    str = `${str.replace(" ", "T")}Z`;
                }
            }
            const date = new Date(str);
            if (isNaN(date.getTime())) return dateStr;

            return date.toLocaleString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
            });
        } catch {
            return dateStr;
        }
    };

    // Calculate total pages for Pagination component
    const totalPages = Math.max(1, Math.ceil(totalRecords / limit));

    return (
        <div className="p-3 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-3 border-b border-gray-700 pb-2 shrink-0">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <Boxes className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-white leading-tight">Containers</h2>
                        <p className="text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {loading ? '...' : (totalRecords > 0 ? `${totalRecords} containers found` : 'No containers found')}
                        </p>
                    </div>
                </div>

                {/* Search and Refresh */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchContainers}
                        disabled={loading}
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                    <div className="relative w-64">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Search containers..."
                            className="w-64 bg-gray-900 border border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500   transition-colors h-8"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Table Area */}
            <div className="overflow-hidden flex flex-col bg-gray-900/40 border border-gray-700/50 rounded-md">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs text-gray-100 border-collapse min-w-[700px]">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <th className="px-3 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer group" onClick={() => handleSort('name')}>
                                    <div className="flex items-center gap-1.5">
                                        <Boxes size={12} className="text-sky-400" /> Container / ID
                                        <SortIcon column="name" />
                                    </div>
                                </th>
                                <th className="px-3 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer group" onClick={() => handleSort('image')}>
                                    <div className="flex items-center gap-1.5">
                                        <Layers size={12} className="text-cyan-400" /> Image
                                        <SortIcon column="image" />
                                    </div>
                                </th>
                                <th className="px-3 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer group" onClick={() => handleSort('runtime')}>
                                    <div className="flex items-center gap-1.5">
                                        <Server size={12} className="text-purple-400" /> Runtime
                                        <SortIcon column="runtime" />
                                    </div>
                                </th>
                                <th className="px-3 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer group" onClick={() => handleSort('state')}>
                                    <div className="flex items-center gap-1.5">
                                        <Activity size={12} className="text-emerald-400" /> State / Status
                                        <SortIcon column="state" />
                                    </div>
                                </th>
                                <th className="px-3 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap min-w-[180px]">
                                    <div className="flex items-center gap-1.5">
                                        <Terminal size={12} className="text-yellow-400" /> Command
                                    </div>
                                </th>
                                <th className="px-3 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer group" onClick={() => handleSort('created')}>
                                    <div className="flex items-center gap-1.5">
                                        <Calendar size={12} className="text-orange-400" /> Created
                                        <SortIcon column="created" />
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/60 bg-gray-900/20 text-xs">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-sky-500" />
                                        Loading containers...
                                    </td>
                                </tr>
                            ) : containerData.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                        <Boxes className="w-6 h-6 text-gray-700 mx-auto mb-2" />
                                        No Containers Found
                                    </td>
                                </tr>
                            ) : (
                                containerData.map((container) => (
                                    <tr key={container.id} className="hover:bg-gray-800/40 transition-colors group align-top">
                                        <td className="px-3 py-2.5 whitespace-nowrap">
                                            <div className="text-[12px] font-bold text-white leading-snug">
                                                {container.name || "-"}
                                            </div>
                                            {container.container_id && (
                                                <div className="text-[10px] font-mono text-gray-400 mt-0.5 flex items-center gap-1">
                                                    <span className="px-1 py-0.2 bg-gray-800 border border-gray-700 rounded text-gray-300">
                                                        {container.container_id.length > 12 ? container.container_id.substring(0, 12) : container.container_id}
                                                    </span>
                                                    {container.pid && container.pid !== "0" && (
                                                        <span className="text-gray-500 text-[9px]">PID: {container.pid}</span>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-[11px]">
                                            <div className="font-mono text-cyan-300/90 font-medium break-all max-w-[260px]" title={container.image}>
                                                {container.image || "-"}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2.5 whitespace-nowrap">
                                            {getRuntimeBadge(container.runtime)}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <div className="flex flex-col gap-1 items-start">
                                                {getStateBadge(container.state)}
                                                {container.status && (
                                                    <span className="text-[10px] text-gray-400 leading-tight" title={container.status}>
                                                        {container.status}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {container.command ? (
                                                <div className="px-2 py-1 bg-black/40 border border-gray-700/50 rounded font-mono text-[10px] text-yellow-300/90 break-all max-h-[60px] overflow-y-auto custom-scrollbar">
                                                    {container.command}
                                                </div>
                                            ) : (
                                                <span className="text-gray-500 text-[11px]">-</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 whitespace-nowrap text-[10px] text-gray-300">
                                            {formatDateFromUTC(container.container_created || container.created)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    itemsPerPage={limit}
                    totalItems={totalRecords}
                    onItemsPerPageChange={(n) => {
                        setLimit(n);
                        setCurrentPage(1);
                    }}
                />
            </div>
        </div>
    );
};

export default AssetContainers;
